import { QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { BrowserRouter } from 'react-router';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v8';
import { AuthProvider } from '@/components/AuthProvider';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { createQueryClient } from '@/lib/queryClient';
import { AppRoutes } from '@/routes';
import { ThemeProvider } from '@/theme/ThemeProvider';

/**
 * Provider order matters:
 *
 *   ErrorBoundary   outermost, so a crash in any provider below still renders a shell
 *   ThemeProvider   installs AntD's StyleProvider/ConfigProvider before anything paints
 *   QueryClient     one client per app instance
 *   BrowserRouter   history, which nuqs needs to write query state onto
 *   NuqsAdapter     binds nuqs to this router; without it, URL state silently no-ops
 *   AuthProvider    innermost of the providers: it seeds the query cache, so it must be
 *                   inside QueryClientProvider, and it navigates on sign-out, so it must
 *                   be inside the router
 *
 * [amended LIGHT-COLOURFUL] There is no `UiModeSync` any more: the light palette is fixed to
 * colourful, so nothing needs revalidating against `GET /auth/config`.
 */
const App = () => {
  // `useState` rather than a module-level singleton: a client created at import time is
  // shared across every test in a file, so state leaks between tests.
  const [queryClient] = useState(createQueryClient);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <NuqsAdapter>
              <AuthProvider>
                <AppRoutes />
              </AuthProvider>
            </NuqsAdapter>
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
